/**
 * Sticky Registry Service
 * Helper functions for tracking state of multiple sticky elements
 *
 */

const EVENT_PREFIX = 'sticky:';
export const MEMBERS_UPDATE = `${EVENT_PREFIX}members-update`;


const PRIORITY = {
  normal: 1,
  medium: 2,
  high: 3,
};


const stickyEls = new Map();
const stickyListeners = new Map();
// a unique value for using as a key in `stickyListeners`
// (a `Symbol` would make more sense if IE11 supported it)
const watchAll = {};


function notifyMembers(priority, element) {
  const evData = { priority };

  const lowerPriorities = new Set();
  stickyEls.forEach((settings, el) => {
    if (settings.priority > priority) {
      return;
    }
    lowerPriorities.add(el);
  });

  stickyListeners.forEach((handlers, el) => {
    // do not notify of its own changes to prevent endless update/notify loop
    if (el === element) {
      return;
    }
    // notify those whose priority is not higher
    // (i.e. that have lower or equal priority, or are not in the registry)
    if (stickyEls.has(el) && !lowerPriorities.has(el)) {
      return;
    }
    handlers.forEach((handler) => {
      try {
        if (typeof handler === 'function') {
          handler(evData);
        } else if ('fire' in handler) {
          handler.fire(MEMBERS_UPDATE, evData);
        }
      } catch (error) {
        console.error(error);
      }
    });
  });
}


function isElementFixed(element) {
  return (getComputedStyle(element).position === 'fixed');
}

/**
 * @returns Rect whose left/right coordinates are relative to document
 * and top/bottom coordinates are relative to viewport
 */
function getBoundingRect(element, isFixed) {
  if (isFixed === undefined) {
    isFixed = isElementFixed(element);
  }
  let { top, right, bottom, left, width, height } = element.getBoundingClientRect();
  // It's possible to determine an element's coordinates relative to document in most cases,
  // except for when it has `position: sticky`,
  // in which case we only can reliably determine left/right coordinates, but not top/bottom,
  // so don't bother with `pageYOffset`,
  // considering actual top/bottom coordinates won't be used for non-fixed elements anyway
  const xOffset = window.pageXOffset;
  if (!isFixed) {
    left += xOffset;
    right += xOffset;
  }
  return { top, right, bottom, left, width, height };
}


const service = {
  get defaultPriorities() {
    return PRIORITY;
  },

  MEMBERS_UPDATE,

  validatePriority(priority) {
    if (isNaN(Number(priority))) {
      if (typeof priority === 'string') {
        priority = PRIORITY[priority];
        if (priority === undefined) {
          throw new TypeError(`Unknown priority name, should be one of ${Object.keys(PRIORITY)}`);
        }
      } else {
        throw new TypeError('Unrecognized priority, should be a number or a name');
      }
    }
    return priority;
  },

  isFixed: isElementFixed,

  getFixedRect(element, {priority = PRIORITY.normal, requestedTop = 'auto'} = {}) {
    priority = service.validatePriority(priority);
    const rect = getBoundingRect(element);
    let allowedTop;
    if (requestedTop === 'auto') {
      allowedTop = service.getAvailableTop(element, {priority, boundingRect: rect});
    } else {
      allowedTop = requestedTop;
    }

    // the element may not be fixed yet, but it requests the vertical space beforehand for when it becomes fixed,
    // so use its "future" top and bottom
    rect.top = allowedTop;
    rect.bottom = allowedTop + rect.height;
    return rect;
  },

  /**
   * @param {Function|Object} handler - function (or event manager, i.e. an object with a method `fire`)
   * that will be called (or have the `MEMBERS_UPDATE` event fired on) when sticky elements are rearranged
   * @param {Element} [element = watchAll] - if provided, the handlers will be called only if
   * this element's priority is lower or equal to that of the element that caused the event
   * or if it's not in the registry
   */
  subscribe(handler, element = watchAll) {
    if (!stickyListeners.has(element)) {
      stickyListeners.set(element, new Set());
    }
    const handlers = stickyListeners.get(element);
    handlers.add(handler);
  },

  /**
   * @param {Function|Object} handler - function or event manager that was previously attached with `subscribe`
   * @param {Element} [element = watchAll]
   */
  unsubscribe(handler, element = watchAll) {
    const handlers = stickyListeners.get(element);
    if (!handlers) {
      return;
    }
    handlers.delete(handler);
    if (element !== watchAll && handlers.size === 0) {
      stickyListeners.delete(element);
    }
  },

  /**
   * Registers an element as sticky so its position will be taken into account
   * when other sticky elements calculate their positions
   */
  add(element, {priority = PRIORITY.normal, requestedTop = 'auto'} = {}) {
    if (stickyEls.has(element)) {
      return service.update(element);
    }
    priority = service.validatePriority(priority);
    const rect = service.getFixedRect(element, { priority, requestedTop });
    stickyEls.set(element, {rect, priority, requestedTop});
    notifyMembers(priority, element);
    return rect.top;
  },

  /**
   * Updates sticky element's dimensions and position
   */
  update(element, {forceNotify = false} = {}) {
    const settings = stickyEls.get(element);
    if (!settings) {
      throw new Error('The element is not in the registry');
    }
    const { priority, requestedTop } = settings;
    const oldRect = settings.rect;
    const rect = service.getFixedRect(element, { priority, requestedTop });
    settings.rect = rect;
    stickyEls.set(element, settings);
    // if there's no change compared to previous state, don't trigger events unless it's forced

    if (
      forceNotify
      || rect.top !== oldRect.top
      || rect.bottom !== oldRect.bottom
      || rect.left !== oldRect.left
      || rect.right !== oldRect.right
    ) {
      notifyMembers(priority, element);
    }
    return rect.top;
  },

  /**
   * Removes the element that is no longer sticky from the registry
   */
  remove(element) {
    const settings = stickyEls.get(element);
    if (!settings) {
      return;
    }
    // added class check, spotlight on news was being removed prematurely
    if (!element.className.includes('sticky--fixed sticky--show')) { stickyEls.delete(element); }
    notifyMembers(settings.priority, element);
  },

  /**
   */
  has(element) {
    return stickyEls.has(element);
  },

  /**
   * For a given element that already is or is willing to be sticky,
   * returns the highest possible top position it could stick at
   * considering there may be other sticky elements above the element
   */
  getAvailableTop(element, {priority = PRIORITY.normal, boundingRect} = {}) {
    priority = service.validatePriority(priority);
    const higher = [];
    stickyEls.forEach((settings, el) => {
      if (el !== element && settings.priority >= priority) {
        higher.push(settings);
      }
    });
    if (higher.length === 0) {
      return 0;
    }
    if (!boundingRect) {
      const savedSettings = stickyEls.get(element);
      if (savedSettings) {
        boundingRect = savedSettings.rect;
      } else {
        boundingRect = getBoundingRect(element);
      }
    }
    const stickyOverlapping = [];
    // find those sticky elements that horizontally overlap with the element because they compete for the page space
    higher.forEach(({rect}) => {
      const horOverlap = (rect.right >= boundingRect.left || rect.left <= boundingRect.right);
      if (horOverlap) {
        stickyOverlapping.push(rect);
      }
    });
    // the element is allowed to stick right below the lowest of those elements
    return Math.max(...stickyOverlapping.map(({bottom}) => bottom));
  },

  /**
   */
  getTopmostPosition(priority = PRIORITY.normal) {
    priority = service.validatePriority(priority);
    const bottoms = [];
    stickyEls.forEach((settings) => {
      if (settings.priority > priority) {
        bottoms.push(settings.rect.bottom);
      }
    });
    return Math.max(...bottoms);
  },

  reset() {
    stickyEls.clear();
    stickyListeners.clear();
  },
};

export default service;
